// gatsby-browser.js
import "./src/global.css"

const CALLRAIL_SCRIPT_ID = "callrail-script"
const CALLRAIL_SCRIPT_SRC =
  "https://cdn.callrail.com/companies/152719966/015a9621411c8b5ca23f/12/swap.js"

let callRailLoadPromise

/**
 * CallRail is a singleton for the lifetime of the Gatsby browser session.
 * Keeping one script/runtime preserves the visitor's assigned tracking number.
 */
const loadCallRail = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null)
  }

  if (window.CallTrk && typeof window.CallTrk.swap === "function") {
    return Promise.resolve(window.CallTrk)
  }

  if (callRailLoadPromise) {
    return callRailLoadPromise
  }

  callRailLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(CALLRAIL_SCRIPT_ID)
    const script = existingScript || document.createElement("script")

    const handleLoad = () => resolve(window.CallTrk || null)
    const handleError = () => {
      callRailLoadPromise = undefined
      reject(new Error("CallRail swap.js failed to load"))
    }

    script.addEventListener("load", handleLoad, { once: true })
    script.addEventListener("error", handleError, { once: true })

    if (!existingScript) {
      script.id = CALLRAIL_SCRIPT_ID
      script.src = CALLRAIL_SCRIPT_SRC
      script.async = true
      document.body.appendChild(script)
    }
  })

  return callRailLoadPromise
}

/**
 * React can replace DOM that CallRail swapped on the previous Gatsby page.
 * CallRail documents CallTrk.swap(node) for rescanning SPA-managed content.
 */
const rescanCallRail = () => {
  loadCallRail()
    .then(callTrk => {
      if (!callTrk || typeof callTrk.swap !== "function") return

      const gatsbyRoot =
        document.getElementById("gatsby-focus-wrapper") ||
        document.getElementById("___gatsby") ||
        document.body

      callTrk.swap(gatsbyRoot)
    })
    .catch(error => {
      console.warn(error.message)
    })
}

const updateSenseiLinks = () => {
  const storedUtm = sessionStorage.getItem("utmParams")
  if (!storedUtm) return

  const links = document.querySelectorAll('a[href*="sensei3d"]')
  links.forEach(link => {
    const currentUrl = link.getAttribute("href")
    if (!currentUrl || currentUrl.includes(storedUtm)) return

    const newUrl = currentUrl.includes("?")
      ? `${currentUrl}&${storedUtm}`
      : `${currentUrl}?${storedUtm}`

    link.setAttribute("href", newUrl)
  })
}

export const onClientEntry = () => {
  loadCallRail().catch(error => {
    console.warn(error.message)
  })
}

export const onRouteUpdate = ({ location }) => {
  // Always remove revamp CSS on route change. ProductTemplate reloads it when
  // the 3D modal opens, so it must not leak onto the destination page.
  const cssLink = document.querySelector(
    'link[href="/static/css/main.5b737e1c.css"]'
  )
  if (cssLink) {
    cssLink.remove()
  }
  document.body.classList.remove("revamp-modal-open")

  const searchParams = new URLSearchParams(location.search)
  const utmParams = []

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith("utm_")) {
      utmParams.push(`${key}=${value}`)
    }
  }

  if (utmParams.length > 0) {
    sessionStorage.setItem("utmParams", utmParams.join("&"))
  }

  // onRouteUpdate runs after Gatsby commits the new page. The animation frame
  // lets React finish any same-commit DOM work before third parties scan it.
  requestAnimationFrame(() => {
    updateSenseiLinks()
    rescanCallRail()
  })
}

export const onPreRouteUpdate = () => {
  // No pre-route action is required.
}
